<template>
  <component
    :is="component"
    :class="[
      variant,
      'direction-' + direction,
      theme,
      'stroke-' + stroke,
      { disabled: disabled },
      { rounded: rounded },
      { 'fit-content': fitContent },
      size,
      { 'router-link-exact-active-with-hash': linkExactActiveWithHash },
      { 'single-line': singleLine },
      { selected: selected },
    ]"
    class="button-base"
    v-bind="props"
    @click="handleBaseClick"
  >
    <GraphicMask
      v-if="icon"
      class="icon"
      :src="icon.src"
      :color="icon.color"
      :size="icon.size"
    />
    <span v-if="$slots.default" class="button-base-content">
      <slot />
    </span>
  </component>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router';
import type { NuxtLinkProps } from '#app';
interface Icon {
  src: string;
  color?: string;
  size?: number;
}
export interface ButtonBaseProps extends NuxtLinkProps {
  theme?: string;
  rounded?: boolean;
  stroke?: '0' | 's' | 'n' | 'm' | 'l';
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  variant?: 'tonal' | 'outlined';
  size?: 'x-small' | 'small' | 'normal' | 'medium' | 'large';
  icon?: Icon;
  disabled?: boolean;
  to?: string | RouteLocationRaw;
  singleLine?: boolean;
  fitContent?: boolean;
  type?: 'button' | 'submit' | 'reset';
  textAlign?: 'left' | 'center' | 'right';
  selected?: boolean;
}

const props = defineProps({
  theme: { default: 'primary' },
  rounded: { type: Boolean, default: false },
  stroke: { default: '0' },
  direction: { default: 'row' },
  variant: { default: 'tonal' },
  size: { default: 'normal' },
  icon: null,
  disabled: { type: Boolean },
  to: null,
  singleLine: { type: Boolean, default: false },
  fitContent: { type: Boolean, default: false },
  type: { default: 'button' },
  textAlign: { default: 'center' },
  selected: { type: Boolean, default: false }
});

const route = useRoute();

const activeElement = useActiveElement();

const linkExactActiveWithHash = computed(() => {
  return toRef(props.to).value === route.fullPath;
});

const component = computed(() => {
  if (props.to || props.href) {
    return resolveComponent('NuxtLink');
  }
  return 'button';
});

const handleBaseClick = () => {
  if (activeElement.value) activeElement.value.blur();
};

const csstextalign = computed(() => props.textAlign);
</script>
<style lang="scss" scoped>
.button-base {
  @include font('m', 500, 'secondary');

  display: inline-flex;
  position: relative;
  flex-grow: 0;
  align-items: center;
  justify-content: center;
  min-height: 4rem;
  padding: var(--gap_n);
  overflow: hidden;
  column-gap: var(--gap_n);
  perspective: 600px;
  perspective-origin: top right;
  text-align: v-bind(csstextalign);
  @include pointer-desktop {
    transition: color 100ms $ease-out-quad 0ms,
      background-color 300ms $ease-out-quad 0ms, border 500ms $ease-out-quad 0ms;
  }

  &.fit-content {
    width: fit-content;
  }
  &.single-line {
    flex: 1 0 auto;
  }
  &.rounded {
    border-radius: 2rem;

    // text-align: center;
  }

  &.large {
    @include font('m', 700, 'secondary');

    min-height: 4.6rem;
    padding: 0.8rem 2rem;
    &.rounded {
      border-radius: 4rem;
    }
  }

  &.medium {
    @include font('m', 700, 'secondary');

    min-height: 4.6rem;
    padding: 0.8rem 1.2rem;
    &.rounded {
      border-radius: 2.5rem;
    }
  }
  &.normal {
    @include font('n', 700, 'secondary');

    min-height: 4.2rem;
    padding: 0.6rem 2rem;
    &.rounded {
      border-radius: 2rem;
    }
  }
  &.small {
    @include font('s', 700, 'secondary');

    min-height: 3.6rem;
    padding: 0.6rem 1.6rem;
    &.rounded {
      border-radius: 2rem;
    }
  }
  &.x-small {
    @include font('xs', 500, 'secondary');

    min-height: 3rem;
    padding: 0.4rem 1.2rem;

    &.rounded {
      display: flex;
      align-items: center;
      border-radius: 1.5rem;
    }
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .icon,
  .button-base-content {
    z-index: 1;
  }

  .button-base-content {
    display: inline;
    width: 100%;
  }

  &:hover,
  &:focus,
  &.active,
  &.disabled,
  &.router-link-exact-active-with-hash,
  &.router-link-active {
    &::before {
      transform: scale(1) translateX(-101%);
      opacity: 1;
    }

    &::after {
      transform: translateX(101%);
    }
  }

  &.active,
  &.disabled,
  &.selected,
  &.router-link-active,
  &.router-link-exact-active-with-hash,
  &.router-link-exact-active {
    cursor: auto;
  }

  @include pointer-desktop {
    &::before,
    &::after {
      transition: transform 0ms $ease-in-out-quad 100ms;
    }

    &::after {
      transition: transform 300ms $ease-in-out-quad;
    }
    &:hover,
    &:focus {
      &::before {
        transition: transform 500ms $ease-in-out-quad;
      }
      &::after {
        transition: transform 0ms;
      }
    }
  }
}
</style>
